import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PackageDetails as StorePackage } from '@vendasta/store';
import { first } from 'rxjs/operators';
import { BusinessService } from '../services/business.service';
import { CookieService } from '../services/cookie.service';
import { GooglePlacesService } from '../services/google-places/google-places.service';
import { PackageInterest } from '../services/package-interest/package-interest';
import { PackageInterestService } from '../services/package-interest/package-interest.service';

const PACKAGE_INTEREST = 'packageInterest';
const PREDICTION = 'prediction';

@Component({
  selector: 'strfrnt-interest-in-package-dialog',
  templateUrl: './interest-in-package-dialog.component.html',
  styleUrls: ['./interest-in-package-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InterestInPackageDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('search', { static: true })
  searchElementRef: ElementRef;

  @Input() salesPerson: any;
  @Input() pkg: StorePackage;

  predictionCtrl: UntypedFormControl;
  packageInterest = new PackageInterest();
  submitting = false;
  businessName: string;

  constructor(
    public dialogRef: MatDialogRef<InterestInPackageDialogComponent>,
    private googlePlacesService: GooglePlacesService,
    private businessService: BusinessService,
    private alertService: SnackbarService,
    private packageInterestService: PackageInterestService,
    private cookieService: CookieService,
  ) {}

  ngOnInit(): void {
    this.predictionCtrl = new UntypedFormControl('', [this.locationValidator]);

    const prediction = this.getCookie(PREDICTION);
    const packageInterest = this.getCookie(PACKAGE_INTEREST);
    if (prediction) {
      this.predictionCtrl.setValue(prediction);
    }
    if (packageInterest) {
      this.packageInterest = packageInterest;
    }

    this.packageInterest.message = `Hi ${this.salesPerson.firstName}, I'm interested in ${this.pkg.name}. Please send me more details or contact me at your earliest convenience.`;
  }

  ngAfterViewInit(): void {
    this.googlePlacesService.init(this.searchElementRef.nativeElement);
  }

  locationValidator(control: UntypedFormControl): any {
    if (control.value.place_id === undefined) {
      return { locationValid: {} };
    }
    return null;
  }

  async onSubmit(): Promise<void> {
    this.submitting = true;
    try {
      const googlePlace = await this.googlePlacesService.googlePlace$.pipe(first()).toPromise();
      this.packageInterest.packageId = this.pkg.solution_id;
      this.packageInterest.packageName = this.pkg.name;
      const googlePlaceIdCookie = this.getCookie(PREDICTION);
      const originalPlaceId = googlePlaceIdCookie ? googlePlaceIdCookie : null;
      let agid = '';
      if (googlePlace) {
        const place_id = googlePlace.place_id;
        if (this.packageInterest.accountGroupId && originalPlaceId === place_id) {
          agid = this.packageInterest.accountGroupId;
        } else {
          if (place_id) {
            this.setCookie(PREDICTION, googlePlace.place_id);
            agid = await this.businessService
              .createBusinessFromPlaceId(
                place_id,
                this.salesPerson.salesPersonId,
                this.pkg.partner_id,
                this.pkg.market_id,
              )
              .toPromise();
          } else {
            agid = await this.businessService
              .createBusiness(
                this.businessName,
                this.salesPerson.salesPersonId,
                this.pkg.partner_id,
                this.pkg.market_id,
              )
              .toPromise();
          }
        }
      } else {
        agid = await this.businessService
          .createBusiness(this.businessName, this.salesPerson.salesPersonId, this.pkg.partner_id, this.pkg.market_id)
          .toPromise();
      }
      this.packageInterest.accountGroupId = agid;
      await this.packageInterestService.setInterestInPackagePublic(this.packageInterest).toPromise();
      this.setCookie(PACKAGE_INTEREST, this.packageInterest);
      this.alertService.openSuccessSnack(`${this.salesPerson.firstName} has been notified.`);
      this.dialogRef.close();
    } catch {
      this.alertService.openErrorSnack(
        'Something went wrong. Refreshing the page might help, but let us know if this keeps happening.',
      );
    } finally {
      this.submitting = false;
    }
  }

  setCookie(name: string, value: any): void {
    const expires = new Date();
    // expire in 5 years
    expires.setFullYear(expires.getFullYear() + 5);
    this.cookieService.set(name, JSON.stringify(value), expires);
  }

  getCookie(name: string): any {
    return JSON.parse(this.cookieService.get(name));
  }
}
